/**
 * BaaS instances for /baas home from MobileService Option B programs listing.
 *
 * `GET /v1/ib/baas/programs` — Prod InstanceUsers only; HML cards appear when API returns a
 * development env (Prod.HmlInstanceId → Dev instance). Each selectable card is one
 * (baasInstanceId, environment) pair.
 *
 * Create: `POST /v1/ib/console/v1/instances` → BaaS `POST /v1/instances` (parent key via
 * `X-Instance-Id`). Update: `PATCH /v1/instances/{id}` (+ scopes/features/limits PUTs).
 */

import { baasProxy, getConsolePrograms, getConsoleInstances } from "./baasConsoleClient";

export const BAAS_INSTANCE_ENVIRONMENTS = ["development", "production"];

export const BAAS_KYB_STATUSES = ["pending", "in_review", "approved", "rejected"];

const mapStatusToKyb = (status, active) => {
  const normalized = String(status || "").trim().toLowerCase();

  if (!active || normalized.includes("inativ") || normalized.includes("indispon")) {
    return "pending";
  }

  if (normalized.includes("aprov") || normalized === "approved" || normalized === "ok") {
    return "approved";
  }

  if (normalized.includes("rejeit") || normalized.includes("reject")) {
    return "rejected";
  }

  if (normalized.includes("anális") || normalized.includes("analis") || normalized.includes("review")) {
    return "in_review";
  }

  return "pending";
};

const buildInitials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "BA";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

export const buildBaasInstanceSelectionId = (baasInstanceId, environment) =>
  `${baasInstanceId}:${String(environment || "").toLowerCase()}`;

export const mapProgramEnvironmentToInstance = (program, envSummary) => {
  // Prefer per-environment instance_id (HML vs Prod may differ, e.g. 1 vs 9).
  const baasInstanceId = Number(
    envSummary?.instance_id ??
      envSummary?.instanceId ??
      program?.instance_id ??
      program?.instanceId,
  );
  const environment = String(envSummary?.environment || "")
    .trim()
    .toLowerCase();
  const name = String(program?.name || program?.program_id || "BaaS").trim();
  const active = Boolean(envSummary?.active);
  const status = envSummary?.status ?? envSummary?.display_name ?? "";
  const hmlInstanceIdRaw =
    program?.hml_instance_id ??
    program?.hmlInstanceId ??
    envSummary?.hml_instance_id ??
    envSummary?.hmlInstanceId;
  const hmlInstanceId = Number(hmlInstanceIdRaw);
  const allowGlobalAccess =
    program?.allow_global_access ??
    program?.allowGlobalAccess ??
    envSummary?.allow_global_access ??
    envSummary?.allowGlobalAccess;

  const isDebug =
    typeof envSummary?.is_debug === "boolean"
      ? envSummary.is_debug
      : typeof envSummary?.isDebug === "boolean"
        ? envSummary.isDebug
        : environment === "development" || environment === "dev"
          ? true
          : undefined;

  return {
    id: buildBaasInstanceSelectionId(baasInstanceId, environment),
    baasInstanceId,
    instanceId: baasInstanceId,
    programId: program?.program_id ?? program?.programId ?? String(baasInstanceId),
    name:
      environment === "development" || environment === "dev"
        ? `${name} (HML)`
        : name,
    displayName: name,
    initials: buildInitials(name),
    environment,
    environmentDisplayName: envSummary?.display_name ?? envSummary?.displayName ?? environment,
    status,
    active,
    isDebug,
    isProduction:
      typeof envSummary?.is_production === "boolean"
        ? envSummary.is_production
        : typeof envSummary?.isProduction === "boolean"
          ? envSummary.isProduction
          : environment === "production" || environment === "prod"
            ? mapStatusToKyb(status, active) === "approved"
            : false,
    hmlInstanceId: hmlInstanceId > 0 ? hmlInstanceId : undefined,
    allowGlobalAccess:
      typeof allowGlobalAccess === "boolean" ? allowGlobalAccess : undefined,
    ownerEntityId: (() => {
      const raw =
        envSummary?.owner_entity_id ??
        envSummary?.ownerEntityId ??
        program?.owner_entity_id ??
        program?.ownerEntityId;
      const id = String(raw || "").trim();
      return id || null;
    })(),
    kybStatus: mapStatusToKyb(status, active),
  };
};

export const mapProgramsToInstances = (programsResponse) => {
  const programs =
    programsResponse?.programs ??
    programsResponse?.Programs ??
    (Array.isArray(programsResponse) ? programsResponse : []);
  if (!Array.isArray(programs)) return [];

  const instances = [];
  const seenSelectionIds = new Set();

  programs.forEach((program) => {
    const environments = program?.environments ?? program?.Environments ?? [];
    if (!Array.isArray(environments) || environments.length === 0) {
      return;
    }

    environments.forEach((envSummary) => {
      const environment = String(envSummary?.environment || envSummary?.Environment || "")
        .trim()
        .toLowerCase();
      if (environment !== "development" && environment !== "production") {
        return;
      }

      const mapped = mapProgramEnvironmentToInstance(program, {
        ...envSummary,
        environment,
      });
      if (!(mapped.baasInstanceId > 0 && mapped.environment)) {
        return;
      }

      // Dedupe identical `{id}:{env}` cards if API returns overlapping programs.
      if (seenSelectionIds.has(mapped.id)) {
        return;
      }
      seenSelectionIds.add(mapped.id);
      instances.push(mapped);
    });
  });

  return instances;
};

const stripHmlDisplaySuffix = (name) =>
  String(name || "")
    .replace(/\s*\(HML\)\s*$/i, "")
    .trim();

const resolveProgramGroupDisplayName = (seed) => {
  const fromDisplay = String(seed?.displayName || "").trim();
  if (fromDisplay) return fromDisplay;
  return stripHmlDisplaySuffix(seed?.name) || "BaaS";
};

/**
 * Collapse flat HML + Production instance cards into one program group.
 * Primary key: `programId` / program_code. Fallback: Prod.`hmlInstanceId` twin link.
 *
 * @returns {Array<{
 *   id: string,
 *   programId: string|null,
 *   name: string,
 *   initials: string,
 *   production: object|null,
 *   development: object|null,
 * }>}
 */
export const groupBaasInstancesByProgram = (instances) => {
  const list = Array.isArray(instances) ? instances : [];
  if (list.length === 0) return [];

  const parent = new Map();
  list.forEach((instance) => {
    parent.set(instance.id, instance.id);
  });

  const find = (id) => {
    let root = parent.get(id) ?? id;
    while ((parent.get(root) ?? root) !== root) {
      root = parent.get(root);
    }
    // Path compression
    let cursor = id;
    while (cursor !== root) {
      const next = parent.get(cursor) ?? cursor;
      parent.set(cursor, root);
      cursor = next;
    }
    return root;
  };

  const union = (a, b) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) parent.set(rootA, rootB);
  };

  // Merge by shared program_code / programId.
  const byProgramId = new Map();
  list.forEach((instance) => {
    const programId = String(instance?.programId || "").trim().toLowerCase();
    if (!programId) return;
    if (byProgramId.has(programId)) {
      union(byProgramId.get(programId), instance.id);
    } else {
      byProgramId.set(programId, instance.id);
    }
  });

  // Merge Prod ↔ HML via HmlInstanceId twin link.
  // Same numeric id is valid (cross-DB pointer) — look up the other env lane, not Map overwrite.
  list.forEach((instance) => {
    const twinId = Number(instance?.hmlInstanceId);
    if (!(twinId > 0)) return;
    const twin =
      list.find(
        (row) =>
          Number(row?.baasInstanceId) === twinId &&
          row.id !== instance.id &&
          isBaasHmlInstance(row) !== isBaasHmlInstance(instance),
      ) ||
      list.find(
        (row) => Number(row?.baasInstanceId) === twinId && row.id !== instance.id,
      );
    if (twin) union(instance.id, twin.id);
  });

  const buckets = new Map();
  list.forEach((instance) => {
    const root = find(instance.id);
    if (!buckets.has(root)) buckets.set(root, []);
    buckets.get(root).push(instance);
  });

  return [...buckets.values()].map((members) => {
    const production = members.find((row) => !isBaasHmlInstance(row)) || null;
    const development = members.find((row) => isBaasHmlInstance(row)) || null;
    const seed = production || development || members[0];
    const programId =
      String(seed?.programId || production?.programId || development?.programId || "").trim() ||
      null;
    const key =
      String(programId || "").trim().toLowerCase() ||
      `solo:${seed.id}`;

    return {
      id: `program:${key}`,
      programId,
      name: resolveProgramGroupDisplayName(seed),
      initials: seed?.initials || buildInitials(resolveProgramGroupDisplayName(seed)),
      production,
      development,
      allowGlobalAccess:
        production?.allowGlobalAccess === true ||
        development?.allowGlobalAccess === true ||
        undefined,
    };
  });
};

/** Whether a program group has the environment implied by the home filter. */
export const programGroupMatchesEnvFilter = (group, envFilter) => {
  if (!group) return false;
  if (envFilter === "all" || !envFilter) return true;
  if (envFilter === "hml") return Boolean(group.development);
  if (envFilter === "production") return Boolean(group.production);
  return true;
};

/**
 * Pick the active lane inside a program card.
 * Top filter HML/Production locks preference; "all" prefers Production when present.
 */
export const resolveProgramGroupActiveInstance = (group, preferredEnv = "all") => {
  if (!group) return null;
  if (preferredEnv === "hml") return group.development || group.production || null;
  if (preferredEnv === "production") return group.production || group.development || null;
  return group.production || group.development || null;
};

export const resolveProgramGroupActiveEnv = (group, preferredEnv = "all") => {
  const active = resolveProgramGroupActiveInstance(group, preferredEnv);
  if (!active) return null;
  return isBaasHmlInstance(active) ? "development" : "production";
};

/** @deprecated Prefer fetchBaasInstances — kept for sync call sites during migration. */
let cachedInstances = [];

/**
 * KYB wizard gate — only for local/mock instances still onboarding.
 * Console-backed cards use env Active/status for the badge, but must not force /baas/kyb.
 */
export const isBaasKybPending = (instance) => {
  if (!instance) return false;
  if (instance.baasInstanceId) return false;
  return (instance.kybStatus ?? "pending") === "pending";
};

export const isBaasInstanceReleased = (instance) => {
  if (!instance) return false;

  // API-backed cards: green "Liberado" only when liberated for production.
  if (instance.baasInstanceId) {
    if (isBaasHmlInstance(instance)) return false;
    if (typeof instance.isProduction === "boolean") return instance.isProduction === true;
    return (instance.kybStatus ?? "pending") === "approved";
  }

  return (instance.kybStatus ?? "pending") === "approved";
};

/** Homologation / staging instances (HML) — simulation tools are allowed. */
export const isBaasHmlInstance = (instance) => {
  if (!instance) return false;
  const env = String(instance.environment ?? "").toLowerCase();
  // Fail closed: production lane is never HML (wizard / sandbox tools).
  if (env === "production" || env === "prod") return false;
  const id = String(instance.id ?? "").toLowerCase();
  if (instance.isDebug === true) return true;
  return (
    env === "development" ||
    env === "dev" ||
    env === "hml" ||
    env === "homologation" ||
    env === "homologacao" ||
    env === "staging" ||
    env === "sandbox" ||
    id.includes(":development") ||
    id.includes(":hml") ||
    id.includes(":dev")
  );
};

/**
 * Production lane waiting for Valut liberação (`is_production` false).
 * HML is never blocked by this gate — partners use HML for homologation.
 */
export const isBaasProdAwaitingLiberation = (instance) => {
  if (!instance?.baasInstanceId) return false;
  if (isBaasHmlInstance(instance)) return false;
  return instance.isProduction !== true;
};

export const fetchBaasInstances = async (options = {}) => {
  try {
    const data = await getConsolePrograms(options);
    const instances = mapProgramsToInstances(data);
    cachedInstances = instances.map((instance) => ({ ...instance }));
    return instances;
  } catch (programsError) {
    // Compat: if /programs fails (e.g. missing HmlInstanceId migration), fall back to /instances
    // and synthesize a production-only card so IB home can still call /ib/... routes.
    try {
      const list = await getConsoleInstances(options);
      const rows = Array.isArray(list) ? list : list?.instances || [];
      const synthesized = {
        programs: rows.map((row) => {
          const baasInstanceId = Number(row.baas_instance_id ?? row.baasInstanceId ?? row.instance_id);
          const name = row.name || row.program_id || "BaaS";
          const active = row.active !== false;
          return {
            program_id: row.program_id ?? row.programId ?? String(baasInstanceId),
            name,
            instance_id: baasInstanceId,
            owner_entity_id: row.owner_entity_id ?? row.ownerEntityId ?? null,
            environments: [
              {
                environment: "production",
                display_name: "Production",
                instance_id: baasInstanceId,
                status: row.status_badge ?? row.statusBadge ?? (active ? "Aprovado" : "Inativo"),
                active,
                owner_entity_id: row.owner_entity_id ?? row.ownerEntityId ?? null,
              },
            ],
          };
        }),
      };
      const instances = mapProgramsToInstances(synthesized);
      cachedInstances = instances.map((instance) => ({ ...instance }));
      return instances;
    } catch {
      throw programsError;
    }
  }
};

export const getMockBaasInstances = () =>
  cachedInstances.map((instance) => ({ ...instance }));

export const getBaasInstanceById = (instanceId) =>
  cachedInstances.find((instance) => instance.id === instanceId) ?? null;

export const getBaasInstanceTypeOptions = () => [...BAAS_INSTANCE_ENVIRONMENTS];

export const getBaasInstanceTeamOptions = () =>
  cachedInstances.map((instance) => ({
    id: instance.id,
    name: instance.name,
  }));

/** Slug for BaaS `program_code` (no fallback — empty input → empty string). */
export const normalizeBaasProgramCode = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);

/** Slug for BaaS `program_code` (required on create). */
export const buildBaasProgramCode = (name) => {
  const slug = normalizeBaasProgramCode(name);
  return slug || `program-${Date.now().toString(36)}`;
};

const isDevelopmentEnvironment = (environment) => {
  const env = String(environment || "").toLowerCase();
  return (
    env === "development" ||
    env === "dev" ||
    env === "hml" ||
    env === "homologation" ||
    env === "homologacao" ||
    env === "staging" ||
    env === "sandbox"
  );
};

/**
 * Parent card whose partner key is used for `POST /v1/instances`.
 * Must match the target environment lane (HML vs Prod) so Option B listing stays consistent.
 */
export const resolveBaasCreateParentInstance = (instances, environment) => {
  const list = Array.isArray(instances) ? instances : [];
  const apiBacked = list.filter((instance) => Number(instance?.baasInstanceId) > 0);
  if (apiBacked.length === 0) return null;

  const wantHml = isDevelopmentEnvironment(environment);
  const matching = apiBacked.filter((instance) => isBaasHmlInstance(instance) === wantHml);
  if (matching.length === 0) return null;

  // Prefer the lowest catalog id — Valut matrix / AllowGlobalAccess anchor is typically first.
  return matching.reduce(
    (best, row) =>
      !best || Number(row.baasInstanceId) < Number(best.baasInstanceId) ? row : best,
    null,
  );
};

const mapCreateResponseToInstance = (response, { environment = "production", isProduction = true } = {}) => {
  const baasInstanceId = Number(response?.instance_id ?? response?.instanceId);
  const env = String(environment || "production").toLowerCase();
  const name = String(response?.name || "").trim() || "BaaS";
  const programId = String(response?.program_code ?? response?.programCode ?? baasInstanceId);
  const liberated = isProduction === true;

  return {
    id: buildBaasInstanceSelectionId(baasInstanceId, env),
    baasInstanceId,
    instanceId: baasInstanceId,
    programId,
    name: isDevelopmentEnvironment(env) ? `${name} (HML)` : name,
    displayName: name,
    initials: buildInitials(name),
    environment: env,
    environmentDisplayName: isDevelopmentEnvironment(env) ? "Development" : "Production",
    status: liberated ? "Aprovado" : isDevelopmentEnvironment(env) ? "Aprovado" : "Não liberado",
    // HML-only rows are usable for homologation; Prod catalog needs liberation.
    active: liberated || isDevelopmentEnvironment(env),
    isProduction: liberated,
    kybStatus: liberated ? "approved" : "pending",
    setupStatus: response?.setup_status ?? response?.setupStatus ?? null,
    ownerEntityId: response?.owner_entity_id ?? response?.ownerEntityId ?? null,
    apiKeys: response?.api_keys ?? response?.apiKeys ?? null,
  };
};

/**
 * Create a BaaS instance via MobileService console proxy → `POST /v1/instances`.
 * Requires an existing parent instance in the target lane. Valut global admins are routed
 * through the AllowGlobalAccess matrix partner key by MobileService (instances.create).
 *
 * - Creates prod catalog in bank_prod; MobileService orchestrates HML row in bank when configured.
 * - `is_production` is always false on create (operational gate; Prod keys blocked until
 *   Valut Aprovar produção). Request flag is ignored.
 * - Use production parent; MS proxy no longer forces HML lane on is_production=false.
 */
export const createBaasInstance = async ({
  name,
  environment = "production",
  isProduction = null,
  createHmlTwin = null,
  linkHmlInstanceId = null,
  parentInstance,
  programCode,
  ownerEntityId = null,
  description = null,
  countryCode = "BR",
  currencies = ["BRL"],
  allowSkipKyc = null,
  allowedIps = null,
  receiveEmail = null,
  templateId = null,
  userAccessTemplateId = null,
  userAccessInviteTemplateId = null,
  rootAccountId = null,
  useVirtualRoot = null,
  registerSmtp = false,
  smtp = null,
  scopes = null,
  features = null,
  pixLimits = null,
  stablesLimits = null,
  efxLimits = null,
  cloneFromInstanceId = null,
} = {}) => {
  const trimmed = String(name || "").trim();
  if (!trimmed) {
    const err = new Error("Instance name is required");
    err.name = "BaasConsoleError";
    err.code = "instance_name_required";
    throw err;
  }

  if (!parentInstance?.baasInstanceId) {
    const err = new Error(
      "An existing BaaS instance with create permission is required in this environment.",
    );
    err.name = "BaasConsoleError";
    err.code = "instance_create_parent_required";
    throw err;
  }

  void createHmlTwin;
  void isProduction; // create always not liberated — Valut Aprovar produção later

  const resolvedIsProduction = false;
  const createEnvironment = "production";
  const explicitProgramCode =
    programCode != null && String(programCode).trim() !== ""
      ? String(programCode).trim().toLowerCase()
      : null;
  const code = explicitProgramCode || buildBaasProgramCode(trimmed);
  if (!code) {
    const err = new Error("program_code is required");
    err.name = "BaasConsoleError";
    err.code = "program_code_required";
    throw err;
  }
  const body = {
    name: trimmed,
    program_code: code,
    country_code: countryCode || undefined,
    currencies: Array.isArray(currencies) && currencies.length > 0 ? currencies : ["BRL"],
    auto_setup: true,
    link_business_account: true,
    create_keys: { partner: false },
    is_production: resolvedIsProduction,
  };

  const cloneFrom = cloneFromInstanceId != null ? Number(cloneFromInstanceId) : 0;
  if (cloneFrom > 0) body.clone_from_instance_id = cloneFrom;

  // Prefer explicit owner; when cloning, fall back to parent/source owner so elevated
  // matrix keys (instances.owner.set) do not 422 when the wizard omitted the field.
  const ownerId = ownerEntityId != null ? String(ownerEntityId).trim() : "";
  const parentOwner =
    parentInstance?.ownerEntityId != null
      ? String(parentInstance.ownerEntityId).trim()
      : "";
  const resolvedOwner = ownerId || (cloneFrom > 0 ? parentOwner : "") || "";
  if (resolvedOwner) body.owner_entity_id = resolvedOwner;

  // Production creates may link an existing twin.
  if (linkHmlInstanceId != null && Number(linkHmlInstanceId) > 0) {
    body.link_hml_instance_id = Number(linkHmlInstanceId);
  }

  const definition = description != null ? String(description).trim() : "";
  if (definition) body.description = definition;

  if (typeof allowSkipKyc === "boolean") body.allow_skip_kyc = allowSkipKyc;

  const ips = allowedIps != null ? String(allowedIps).trim() : "";
  if (ips) body.allowed_ips = ips;

  if (typeof receiveEmail === "boolean") body.receive_email = receiveEmail;

  const template = templateId != null ? String(templateId).trim() : "";
  if (template) body.template_id = template;

  const userAccessTemplate =
    userAccessTemplateId != null ? String(userAccessTemplateId).trim() : "";
  if (userAccessTemplate) body.user_access_template_id = userAccessTemplate;

  const userAccessInviteTemplate =
    userAccessInviteTemplateId != null ? String(userAccessInviteTemplateId).trim() : "";
  if (userAccessInviteTemplate) body.user_access_invite_template_id = userAccessInviteTemplate;

  const rootId = rootAccountId != null ? String(rootAccountId).trim() : "";
  if (useVirtualRoot === true) {
    body.use_virtual_root = true;
  } else if (rootId) {
    body.root_account_id = rootId;
  }

  if (registerSmtp) {
    body.register_smtp = true;
    const smtpPayload = smtp && typeof smtp === "object" ? smtp : {};
    body.smtp = {
      host: smtpPayload.host || undefined,
      port: smtpPayload.port != null && smtpPayload.port !== "" ? Number(smtpPayload.port) : undefined,
      username: smtpPayload.username || undefined,
      password: smtpPayload.password || undefined,
      from_email: smtpPayload.fromEmail || smtpPayload.from_email || undefined,
      from_name: smtpPayload.fromName || smtpPayload.from_name || undefined,
      api_key: smtpPayload.apiKey || smtpPayload.api_key || undefined,
    };
  }

  if (Array.isArray(scopes) && scopes.length > 0) {
    body.scopes = scopes.map((scope) => String(scope).trim()).filter(Boolean);
  }

  if (Array.isArray(features) && features.length > 0) {
    body.features = features
      .map((item) => ({
        feature_code: String(item?.feature_code || item?.featureCode || "").trim(),
        enabled: Boolean(item?.enabled),
      }))
      .filter((item) => item.feature_code);
  }

  if (Array.isArray(pixLimits) && pixLimits.length > 0) {
    body.pix_limits = pixLimits.map((policy) => ({
      type: Number(policy?.type ?? 0),
      period: Number(policy?.period ?? 0),
      period_hour_start: Number(policy?.period_hour_start ?? policy?.periodHourStart ?? 6),
      period_hour_end: Number(policy?.period_hour_end ?? policy?.periodHourEnd ?? 19),
      amount: Number(policy?.amount ?? 0),
    }));
  }

  if (Array.isArray(stablesLimits) && stablesLimits.length > 0) {
    body.stables_limits = stablesLimits.map((policy) => ({
      operation_type: String(policy?.operation_type || policy?.operationType || "").trim(),
      period_type: String(policy?.period_type || policy?.periodType || "hourly").trim(),
      limit_amount_cents: Number(
        policy?.limit_amount_cents ?? policy?.limitAmountCents ?? 0,
      ),
    }));
  }

  if (Array.isArray(efxLimits) && efxLimits.length > 0) {
    body.efx_limits = efxLimits.map((policy) => ({
      operation_type: String(policy?.operation_type || policy?.operationType || "").trim(),
      period_type: String(policy?.period_type || policy?.periodType || "hourly").trim(),
      limit_amount_cents: Number(
        policy?.limit_amount_cents ?? policy?.limitAmountCents ?? 0,
      ),
    }));
  }

  const response = await baasProxy({
    instance: parentInstance,
    method: "POST",
    baasPath: "v1/instances",
    body,
  });

  const created = mapCreateResponseToInstance(response, {
    environment: createEnvironment,
    isProduction: resolvedIsProduction,
  });
  if (response?.hml_instance_id || response?.hmlInstanceId) {
    created.hmlInstanceId = Number(response.hml_instance_id ?? response.hmlInstanceId);
  }
  if (typeof response?.is_production === "boolean" || typeof response?.isProduction === "boolean") {
    created.isProduction = Boolean(response.is_production ?? response.isProduction);
    created.status = created.isProduction ? "Aprovado" : "Não liberado";
    // HML-only instances are active for homologation even when not liberated for Prod.
    created.active = created.isProduction || isDevelopmentEnvironment(createEnvironment);
    created.kybStatus = created.isProduction ? "approved" : "pending";
  }
  cachedInstances = [...cachedInstances.filter((row) => row.id !== created.id), created];
  return { instance: created, response };
};

/**
 * Patch instance catalog flags via `PATCH /v1/instances/{id}`
 * (name / description / access / SMTP / is_production / HML / roots).
 */
export const updateBaasInstance = async ({
  instance,
  name = null,
  description = null,
  isProduction = null,
  isDebug = null,
  carteiraWhiteLabel = null,
  productionRequested = null,
  active = null,
  createHmlTwin = null,
  linkHmlInstanceId = null,
  unlinkHml = false,
  countryCode = null,
  currencies = null,
  allowSkipKyc = null,
  inboundPixOriginLockEnabled = null,
  inboundPixOriginDocument = null,
  pixLimitsUnlimited = null,
  operatingAllowNegative = null,
  allowOverBalance = null,
  allowedIps = null,
  receiveEmail = null,
  templateId = null,
  userAccessTemplateId = null,
  userAccessInviteTemplateId = null,
  rootAccountId = null,
  useVirtualRoot = null,
  otcRootAccountId = null,
  registerSmtp = false,
  clearSmtp = false,
  smtp = null,
  parentInstance = null,
  defaultPartnerScopes = undefined,
  branding = undefined,
  appConfig = undefined,
  publicHost = undefined,
  dnsAliases = undefined,
} = {}) => {
  const target = instance || parentInstance;
  if (!target?.baasInstanceId) {
    const err = new Error("Instance is required for update.");
    err.name = "BaasConsoleError";
    err.code = "instance_update_required";
    throw err;
  }

  const body = {};
  if (name != null) body.name = String(name).trim();
  if (description != null) body.description = String(description).trim();
  if (typeof isProduction === "boolean") body.is_production = isProduction;
  if (typeof isDebug === "boolean") body.is_debug = isDebug;
  if (typeof carteiraWhiteLabel === "boolean") body.carteira_white_label = carteiraWhiteLabel;
  if (typeof productionRequested === "boolean") body.production_requested = productionRequested;
  if (typeof active === "boolean") body.active = active;
  if (typeof createHmlTwin === "boolean") body.create_hml_twin = createHmlTwin;
  if (linkHmlInstanceId != null) body.link_hml_instance_id = Number(linkHmlInstanceId);
  if (unlinkHml) body.unlink_hml = true;
  if (countryCode != null) body.country_code = String(countryCode).trim();
  if (Array.isArray(currencies) && currencies.length > 0) body.currencies = currencies;
  if (typeof allowSkipKyc === "boolean") body.allow_skip_kyc = allowSkipKyc;
  if (typeof inboundPixOriginLockEnabled === "boolean") {
    body.inbound_pix_origin_lock_enabled = inboundPixOriginLockEnabled;
  }
  if (inboundPixOriginDocument != null) {
    body.inbound_pix_origin_document = String(inboundPixOriginDocument).trim();
  }
  if (typeof pixLimitsUnlimited === "boolean") {
    body.pix_limits_unlimited = pixLimitsUnlimited;
  }
  if (typeof operatingAllowNegative === "boolean") {
    body.operating_allow_negative = operatingAllowNegative;
  }
  if (typeof allowOverBalance === "boolean") {
    body.allow_over_balance = allowOverBalance;
  }
  if (allowedIps != null) body.allowed_ips = String(allowedIps).trim();
  if (typeof receiveEmail === "boolean") body.receive_email = receiveEmail;
  if (templateId != null) body.template_id = String(templateId).trim();
  if (userAccessTemplateId != null) body.user_access_template_id = String(userAccessTemplateId).trim();
  if (userAccessInviteTemplateId != null) {
    body.user_access_invite_template_id = String(userAccessInviteTemplateId).trim();
  }
  if (rootAccountId != null) body.root_account_id = String(rootAccountId).trim();
  if (useVirtualRoot === true) body.use_virtual_root = true;
  if (otcRootAccountId != null) body.otc_root_account_id = String(otcRootAccountId).trim();
  if (clearSmtp) body.clear_smtp = true;
  if (defaultPartnerScopes !== undefined) {
    body.default_partner_scopes = Array.isArray(defaultPartnerScopes)
      ? defaultPartnerScopes.map((scope) => String(scope).trim()).filter(Boolean)
      : [];
  }
  if (branding !== undefined && branding != null && typeof branding === "object") {
    body.branding = branding;
  }
  if (appConfig !== undefined && appConfig != null && typeof appConfig === "object") {
    body.app_config = appConfig;
  }
  if (publicHost !== undefined) {
    body.public_host = publicHost == null ? null : String(publicHost).trim() || null;
  }
  if (dnsAliases !== undefined) {
    body.dns_aliases = Array.isArray(dnsAliases)
      ? dnsAliases.map((host) => String(host).trim().toLowerCase()).filter(Boolean)
      : [];
  }
  if (registerSmtp) {
    body.register_smtp = true;
    const smtpPayload = smtp && typeof smtp === "object" ? smtp : {};
    body.smtp = {
      host: smtpPayload.host || undefined,
      port: smtpPayload.port != null && smtpPayload.port !== "" ? Number(smtpPayload.port) : undefined,
      username: smtpPayload.username || undefined,
      password: smtpPayload.password || undefined,
      from_email: smtpPayload.fromEmail || smtpPayload.from_email || undefined,
      from_name: smtpPayload.fromName || smtpPayload.from_name || undefined,
      api_key: smtpPayload.apiKey || smtpPayload.api_key || undefined,
    };
  }

  // Always PATCH the target instance id. Never POST /v1/instances (create).
  // `parentInstance` is accepted for call-site compat but must not redirect auth/path
  // to the create-parent card — that historically looked like a create against an
  // existing name/program_code.
  void parentInstance;
  const response = await baasProxy({
    instance: target,
    method: "PATCH",
    baasPath: `v1/instances/${Number(target.baasInstanceId)}`,
    body,
  });

  // PATCH now returns InstanceDetailResponse (same as GET) including over-balance flags.
  return {
    response,
    detail: {
      operatingAllowNegative: Boolean(
        response?.operating_allow_negative ?? response?.operatingAllowNegative,
      ),
      allowOverBalance: Boolean(
        response?.allow_over_balance ?? response?.allowOverBalance,
      ),
    },
  };
};

/**
 * Full instance detail for settings edit — `GET /v1/instances/{id}`.
 */
export const fetchBaasInstanceDetail = async (instance) => {
  if (!instance?.baasInstanceId) {
    const err = new Error("Instance is required.");
    err.name = "BaasConsoleError";
    err.code = "instance_required";
    throw err;
  }

  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/instances/${Number(instance.baasInstanceId)}`,
  });

  return {
    instanceId: data?.instance_id ?? data?.instanceId ?? Number(instance.baasInstanceId),
    name: data?.name ?? "",
    description: data?.description ?? "",
    programCode: data?.program_code ?? data?.programCode ?? "",
    countryCode: data?.country_code ?? data?.countryCode ?? "BR",
    supportedCurrencies: Array.isArray(data?.supported_currencies)
      ? data.supported_currencies
      : Array.isArray(data?.supportedCurrencies)
        ? data.supportedCurrencies
        : [],
    isProduction: Boolean(data?.is_production ?? data?.isProduction),
    isDebug: Boolean(data?.is_debug ?? data?.isDebug),
    carteiraWhiteLabel: Boolean(
      data?.carteira_white_label ?? data?.carteiraWhiteLabel,
    ),
    productionRequestedAt:
      data?.production_requested_at ?? data?.productionRequestedAt ?? null,
    hmlInstanceId: data?.hml_instance_id ?? data?.hmlInstanceId ?? null,
    allowSkipKyc: Boolean(data?.allow_skip_kyc ?? data?.allowSkipKyc),
    inboundPixOriginLockEnabled: Boolean(
      data?.inbound_pix_origin_lock_enabled ?? data?.inboundPixOriginLockEnabled,
    ),
    inboundPixOriginDocument:
      data?.inbound_pix_origin_document ?? data?.inboundPixOriginDocument ?? "",
    operatingAllowNegative: Boolean(
      data?.operating_allow_negative ?? data?.operatingAllowNegative,
    ),
    allowOverBalance: Boolean(
      data?.allow_over_balance ?? data?.allowOverBalance,
    ),
    allowedIps: data?.allowed_ips ?? data?.allowedIps ?? "",
    receiveEmail:
      typeof (data?.receive_email ?? data?.receiveEmail) === "boolean"
        ? data?.receive_email ?? data?.receiveEmail
        : true,
    emailRoutingMode:
      data?.email_routing_mode ?? data?.emailRoutingMode ?? null,
    templateId: data?.template_id ?? data?.templateId ?? "",
    userAccessTemplateId:
      data?.user_access_template_id ?? data?.userAccessTemplateId ?? "",
    userAccessInviteTemplateId:
      data?.user_access_invite_template_id ?? data?.userAccessInviteTemplateId ?? "",
    defaultPartnerScopes: Array.isArray(data?.default_partner_scopes)
      ? data.default_partner_scopes.map(String)
      : Array.isArray(data?.defaultPartnerScopes)
        ? data.defaultPartnerScopes.map(String)
        : [],
    rootAccountId: data?.root_account_id ?? data?.rootAccountId ?? "",
    otcRootAccountId: data?.otc_root_account_id ?? data?.otcRootAccountId ?? "",
    ownerEntityId: data?.owner_entity_id ?? data?.ownerEntityId ?? "",
    smtp: {
      configured: Boolean(data?.smtp?.configured),
      host: data?.smtp?.host || "smtp.sendgrid.net",
      port: data?.smtp?.port != null ? String(data.smtp.port) : "587",
      username: data?.smtp?.username || "apikey",
      fromEmail: data?.smtp?.from_email ?? data?.smtp?.fromEmail ?? "",
      fromName: data?.smtp?.from_name ?? data?.smtp?.fromName ?? "",
      hasPassword: Boolean(data?.smtp?.has_password ?? data?.smtp?.hasPassword),
      password: "",
    },
    setupStatus: data?.setup_status ?? data?.setupStatus ?? null,
    branding:
      data?.branding && typeof data.branding === "object" ? data.branding : {},
    appConfig:
      (data?.app_config && typeof data.app_config === "object"
        ? data.app_config
        : null) ||
      (data?.appConfig && typeof data.appConfig === "object" ? data.appConfig : {}) ||
      {},
    publicHost: data?.public_host ?? data?.publicHost ?? "",
    dnsAliases: Array.isArray(data?.dns_aliases)
      ? data.dns_aliases.map(String)
      : Array.isArray(data?.dnsAliases)
        ? data.dnsAliases.map(String)
        : [],
    isMatrix: Boolean(
      data?.is_matrix ??
        data?.isMatrix ??
        instance?.allowGlobalAccess,
    ),
    raw: data,
  };
};

/**
 * Replace partner scopes — `PUT /v1/instances/{id}/scopes`.
 */
export const setBaasInstanceScopes = async (instance, scopes = []) => {
  const id = Number(instance?.baasInstanceId);
  return baasProxy({
    instance,
    method: "PUT",
    baasPath: `v1/instances/${id}/scopes`,
    body: { scopes: (scopes || []).map((s) => String(s).trim()).filter(Boolean) },
  });
};

/**
 * Replace product features — `PUT /v1/instances/{id}/features`.
 */
export const setBaasInstanceFeatures = async (instance, features = []) => {
  const id = Number(instance?.baasInstanceId);
  return baasProxy({
    instance,
    method: "PUT",
    baasPath: `v1/instances/${id}/features`,
    body: {
      features: (features || [])
        .map((item) => ({
          feature_code: String(item?.feature_code || item?.featureCode || item?.code || "").trim(),
          enabled: Boolean(item?.enabled),
        }))
        .filter((item) => item.feature_code),
    },
  });
};

/**
 * List instance features — `GET /v1/instances/{id}/features`.
 */
export const fetchBaasInstanceFeatures = async (instance) => {
  const id = Number(instance?.baasInstanceId);
  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/instances/${id}/features`,
  });
  const list = Array.isArray(data?.features) ? data.features : Array.isArray(data) ? data : [];
  return list.map((item) => ({
    featureCode: String(item?.feature_code || item?.featureCode || "").trim(),
    enabled: Boolean(item?.enabled ?? item?.Enabled ?? true),
  }));
};

/** Instance stables ceilings — `GET /v1/instances/{id}/stables/limits`. */
export const getInstanceStablesLimits = async (instance) => {
  const id = Number(instance?.baasInstanceId);
  return baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/instances/${id}/stables/limits`,
  });
};

/** Soft-replace instance stables ceilings — `PUT /v1/instances/{id}/stables/limits`. */
export const setInstanceStablesLimits = async (instance, policies = []) => {
  const id = Number(instance?.baasInstanceId);
  return baasProxy({
    instance,
    method: "PUT",
    baasPath: `v1/instances/${id}/stables/limits`,
    body: { policies },
  });
};

/** Instance stables fees — `GET /v1/instances/{id}/stables/fees`. */
export const getInstanceStablesFees = async (instance) => {
  const id = Number(instance?.baasInstanceId);
  return baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/instances/${id}/stables/fees`,
  });
};

/**
 * Soft-replace instance stables fees — `PUT /v1/instances/{id}/stables/fees`.
 * @param {object} instance
 * @param {{ policies?: object[], networkFees?: object[] }} payload
 */
export const setInstanceStablesFees = async (
  instance,
  { policies = [], networkFees = [] } = {},
) => {
  const id = Number(instance?.baasInstanceId);
  return baasProxy({
    instance,
    method: "PUT",
    baasPath: `v1/instances/${id}/stables/fees`,
    body: {
      policies,
      network_fees: networkFees,
    },
  });
};

/** Instance PIX fees — `GET /v1/instances/{id}/pix/fees`. */
export const getInstancePixFees = async (instance) => {
  const id = Number(instance?.baasInstanceId);
  return baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/instances/${id}/pix/fees`,
  });
};

/** Soft-replace instance PIX fees — `PUT /v1/instances/{id}/pix/fees`. */
export const setInstancePixFees = async (instance, policies = []) => {
  const id = Number(instance?.baasInstanceId);
  return baasProxy({
    instance,
    method: "PUT",
    baasPath: `v1/instances/${id}/pix/fees`,
    body: { policies },
  });
};

/** Instance EFX fees — `GET /v1/instances/{id}/efx/fees`. */
export const getInstanceEfxFees = async (instance) => {
  const id = Number(instance?.baasInstanceId);
  return baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/instances/${id}/efx/fees`,
  });
};

/** Soft-replace instance EFX fees — `PUT /v1/instances/{id}/efx/fees`. */
export const setInstanceEfxFees = async (instance, policies = []) => {
  const id = Number(instance?.baasInstanceId);
  return baasProxy({
    instance,
    method: "PUT",
    baasPath: `v1/instances/${id}/efx/fees`,
    body: { policies },
  });
};

/** Instance Over Balance fees — `GET /v1/instances/{id}/overdraft/fees`. */
export const getInstanceOverdraftFees = async (instance) => {
  const id = Number(instance?.baasInstanceId);
  return baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/instances/${id}/overdraft/fees`,
  });
};

/** Upsert instance Over Balance fees — `PUT /v1/instances/{id}/overdraft/fees`. */
export const setInstanceOverdraftFees = async (instance, body = {}) => {
  const id = Number(instance?.baasInstanceId);
  return baasProxy({
    instance,
    method: "PUT",
    baasPath: `v1/instances/${id}/overdraft/fees`,
    body,
  });
};

/** Instance EFX ceilings — `GET /v1/instances/{id}/efx/limits`. */
export const getInstanceEfxLimits = async (instance) => {
  const id = Number(instance?.baasInstanceId);
  return baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/instances/${id}/efx/limits`,
  });
};

/** Replace instance EFX ceilings — `PUT /v1/instances/{id}/efx/limits`. */
export const setInstanceEfxLimits = async (instance, policies = []) => {
  const id = Number(instance?.baasInstanceId);
  return baasProxy({
    instance,
    method: "PUT",
    baasPath: `v1/instances/${id}/efx/limits`,
    body: { policies },
  });
};

/**
 * Local-only patch for mock/KYB cards. API-backed metadata uses updateBaasInstance.
 */
export const updateMockBaasInstance = (
  instanceId,
  { name, environment, kybStatus, kyb, isProduction, active } = {},
  existingInstance = null,
) => {
  const source = existingInstance ?? getBaasInstanceById(instanceId);
  if (!source) {
    return null;
  }

  // Server-backed cards: only allow local KYB bookkeeping fields, not rename/env.
  if (source.baasInstanceId && (name !== undefined || environment !== undefined)) {
    if (
      kybStatus === undefined &&
      kyb === undefined &&
      typeof isProduction !== "boolean" &&
      typeof active !== "boolean"
    ) {
      return { ...source };
    }
  }

  const nextName = name?.trim() ?? source.name;
  const nextEnvironment = environment ?? source.environment;
  const nextIsProduction =
    typeof isProduction === "boolean" ? isProduction : source.isProduction;

  const updated = {
    ...source,
    name: nextName,
    initials: buildInitials(nextName),
    environment: nextEnvironment,
    ...(typeof active === "boolean"
      ? {
          active,
          status: active ? "Aprovado" : "Não liberado",
        }
      : {}),
    ...(typeof isProduction === "boolean"
      ? {
          isProduction: nextIsProduction,
          status: isProduction ? "Aprovado" : "Não liberado",
          active: isProduction ? true : source.active,
          kybStatus: isProduction ? "approved" : "pending",
        }
      : {}),
    ...(kybStatus !== undefined ? { kybStatus } : {}),
    ...(kyb !== undefined ? { kyb } : {}),
  };

  const index = cachedInstances.findIndex((instance) => instance.id === instanceId);
  if (index >= 0) {
    cachedInstances[index] = updated;
  }

  return { ...updated };
};

/** @deprecated Use createBaasInstance — kept for tests/legacy call sites. */
export const createMockBaasInstance = ({ name, environment }) => {
  const trimmed = String(name || "").trim();
  const env = String(environment || "development").toLowerCase();
  const localId = buildBaasProgramCode(trimmed);

  const created = {
    id: `local:${localId || Date.now()}:${env}`,
    baasInstanceId: null,
    instanceId: null,
    programId: localId,
    name: trimmed,
    displayName: trimmed,
    initials: buildInitials(trimmed),
    environment: env,
    environmentDisplayName: env,
    status: "Pendente",
    active: false,
    kybStatus: "pending",
    isLocalOnly: true,
  };

  cachedInstances = [...cachedInstances, created];
  return { ...created };
};
